<template>
  <div class="vap-page">
    <Loading
      id="throbber"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div v-show="showParentFolder" class="vap-page-header">
      <!-- TOOLBAR -->
        <h1 id="title" :class="{ 'new-parent-folder-name' : isNewParentFolder }">
          {{ folderTitle }}
        </h1>
        <div class="btns-container">
          <VasionButton
          id="save-button"
          class="last-btn"
          :classProp="'primary'"
          :isDisabled="!isDirty"
          @vasionButtonClicked="save()"
        >
          Save
        </VasionButton>

        <VasionButton
          id="cancel-button"
          :classProp="'secondary'"
          @vasionButtonClicked="cancelClick()"
        >
          Cancel
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">

      <!-- TABS -->
      <div class="md-layout">
        <VasionButton
          id="tab-general"
          :classProp="calculatedClass('general')"
          @vasionButtonClicked="updateSelectedTab('general')"
        >
          General
        </VasionButton>

        <VasionButton
          id="tab-permissions"
          :classProp="calculatedClass('permissions')"
          @vasionButtonClicked="updateSelectedTab('permissions')"
        >
          Permissions
        </VasionButton>
      </div>

      <!-- GENERAL -->
      <div v-show="selectedTabName === 'general'" id="general" class="tab">
        <ParentFolderGeneral
          ref="general"
          :defaultDriveLocationId="defaultDriveLocationId"
          :showOnlyFolderName="localShowOnlyFolderName"
          @markAsDirty="markAsDirty"
          @markRequiredInputsComplete="markRequiredInputsComplete"
          @markRequiredInputsNotComplete="markRequiredInputsNotComplete"
          @setParentFolderName="setParentFolderName"
        />
      </div>

      <!-- PERMISSIONS -->
      <div v-show="selectedTabName === 'permissions'" id="permissions" class="tab">
        <PortalParentFolderPermissions
          v-if="localShowOnlyFolderName || localIsPortalFolder"
          :showPortalUsers="localShowOnlyFolderName"
          ref="permissions"
          @markAsDirty="markAsDirty"
        />
        <ParentFolderPermissions
          v-else
          ref="permissions"
          @markAsDirty="markAsDirty"
        />
      </div>

      <VasionSnackbar
        id="results-snack"
        ref="resultsSnack"
        :showSnackbarBool.sync="showSnackbarBool"
        :snackbarImage="snackbarImage"
        :snackbarSubTitle="snackbarSubTitle"
        :snackbarTitle="snackbarTitle"
      />

      <md-dialog id="confirmLeave" :md-active.sync="showLeaveDialog" @md-clicked-outside="clickedOutsideLeave()">
        <VasionConfirmationDialog :message="leaveMessage" @noButtonClick="cancelLeave()" @yesButtonClick="doLeave()" />
      </md-dialog>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import ParentFolderGeneral from '@/components/storage/ParentFolderGeneral.vue';
import ParentFolderPermissions from '@/components/storage/ParentFolderPermissions.vue';
import PortalParentFolderPermissions from '@/components/storage/PortalParentFolderPermissions.vue';

export default {
  name: 'TheParentFolder',
  components: {
    Loading,
    ParentFolderGeneral,
    ParentFolderPermissions,
    PortalParentFolderPermissions,
  },
  beforeRouteLeave(to, from, next) {
    if (this.$store.state.common.bypassNavigationGuards) return next()
    
    if (this.isDirty && !this.routeTo) {
      this.routeTo = to
      this.toggleLeaveDialog()
    } else {
      this.routeTo = null
      next()
    }
  },
  props: {
    defaultDriveLocationId: {
      type: Number,
      default: null,
      required: false,
    },
    isModal: {
      type: Boolean,
      default: false,
      required: false,
    },
    showOnlyFolderName: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: function () {
    return {
      activeFolder: {},
      areRequiredInputsComplete: false,
      areRequiredInputsCompleteAndIsDirty: false,
      dataLoaded: false,
      encrypted: false,
      encryptionPassword: '',
      groupAccess: [],
      groupReadOnlyFolderSecurity: [
        {
          canDelete: false,
          canEmail: false,
          canExport: false,
          canModify: false,
          canPrint: false,
          canViewNative: false,
          entityID: 0,
          folderID: 0,
        },
      ],
      folderTitle: '',
      isDirty: false,
      isLoading: false,
      isNewParentFolder: true,
      isSubfolder: false,
      localIsPortalFolder: false,
      localShowOnlyFolderName: false, // Local data property for showOnlyFolderName
      loaderColor: '#ff4702',
      loaderBackgroundColor: '#ece7ec',
      parentFolderId: null,
      parentFolderName: '',
      piiData: false,
      readOnly: false,
      selectedTabName: 'general',
      showLeaveDialog: false,
      showParentFolder: true,
      showSnackbarBool: false,
      snackbarCloseOnClick: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      storageType: '',
      userAccess: [],
      userReadOnlyFolderSecurity: [
        {
          canDelete: false,
          canEmail: false,
          canExport: false,
          canModify: false,
          canPrint: false,
          canViewNative: false,
          entityID: 0,
          folderID: 0,
        },
      ],
      vaultID: 0,
      vaultName: '',
    }
  },
  computed: {
    isInternalParentFolder() {
      return this.parentFolderType === 'internal'
    },
    isPortalParentFolder() {
      return this.parentFolderType === 'portal'
    },
    leaveMessage() {
      return this.parentFolderName.length > 0
        ? `Are you sure you want to leave? Changes to "${this.parentFolderName}" have not been saved.`
        : `Are you sure you want to leave? Changes to your ${this.isSubfolder ? 'Untitled Subfolder' : 'Untitled Parent Folder'} have not been saved.`
    },
    parentFolderType() {
      return this.$store.state.vault.parentFolderType
    },
  },
  async created() {
    // Initialize localShowOnlyFolderName with the prop value
    this.localShowOnlyFolderName = this.showOnlyFolderName
    
    // Check if showOnlyFolderName is set in the query parameters
    if (this.$route.query.showOnlyFolderName === true) {
      this.localShowOnlyFolderName = true
      this.selectedTabName = 'general' // Ensure general tab is selected when only showing folder name
    }
    
    if (this.$route.query.isPortalFolder) {
      this.localIsPortalFolder = true
    }
    

    // Check if this is a subfolder creation or editing
    if (this.$route.query.parentFolderId) {
      this.parentFolderId = parseInt(this.$route.query.parentFolderId, 10)
      
      if (this.$route.query.isSubfolder) {
        this.isSubfolder = true
        this.folderTitle = 'New Subfolder'
      }
    }
    
    await this.loadActiveFolderData()

    if (this.activeFolder && this.activeFolder.vaultID && this.activeFolder.vaultID > 0) {
      await this.setComponentDetails(this.activeFolder.vaultID, this.activeFolder)
    }

    this.folderTitle = this.activeFolder && this.activeFolder.vaultName ? this.activeFolder.vaultName : (this.isSubfolder ? 'Untitled Subfolder' : 'Untitled Parent Folder')
  },
  async mounted() {
    await this.loadActiveFolderData()

    if (!this.activeFolder || this.activeFolder.vaultID === undefined || this.activeFolder.vaultID <= 0) {
      // special case where if there's no folder data, we actually want to load things in mounted to avoid a race condition
      await this.setComponentDetails(0, null)
    }
  },
  methods: {
    calculatedClass(selectedTab) { return this.selectedTabName === selectedTab ? 'text-selected' : 'text' },
    cancelClick() {
      this.$emit('close')
      if (!this.isModal) {
        this.$router.push({ name: 'ParentFolders', params: { lastActiveTab: this.parentFolderType } })
      }
    },
    cancelLeave() {
      this.routeTo = null
      this.toggleLeaveDialog()
    },
    doLeave() {
      this.toggleLeaveDialog()
      if (this.routeTo && this.routeTo.name !== '') {
        this.$router.push({ name: this.routeTo.name });
        this.routeTo = null
      }
    },
    async loadActiveFolderData() {
      if (this.dataLoaded || !this.$route.params.folderId || parseInt(this.$route.params.folderId, 10) <= 0) {
        return
      }

      this.isNewParentFolder = false
      this.activeFolder = await this.$store.dispatch('vault/getSingleVault', parseInt(this.$route.params.folderId, 10))
      this.dataLoaded = true
    },
    markAsClean() { this.isDirty = false },
    markAsDirty() {
      this.isDirty = true
      if (this.areRequiredInputsComplete) {
        this.areRequiredInputsCompleteAndIsDirty = true
      } else {
        this.areRequiredInputsCompleteAndIsDirty = false
      }
    },
    markRequiredInputsComplete() {
      this.areRequiredInputsComplete = true
      if (this.isDirty) {
        this.areRequiredInputsCompleteAndIsDirty = true
      }
    },
    markRequiredInputsNotComplete() {
      this.areRequiredInputsComplete = false
      this.areRequiredInputsCompleteAndIsDirty = false
    },
    async save() {
      this.isLoading = true
      this.$refs.resultsSnack.closeSnackbar()
      let decryptResult = {}
      const generalData = await this.$refs.general.buildGeneralSaveDataJSON()
      let result = { Value: "True" }; // Default value in case we skip saveFolderIndexSchedule
      
      // Only call saveFolderIndexSchedule if we're not in showOnlyFolderName mode
      if (!this.localShowOnlyFolderName && generalData.folderIndexSchedule) {
        result = await this.$store.dispatch('search/saveFolderIndexSchedule', generalData.folderIndexSchedule)
        if (result.Value === "False"){
          this.isLoading = false
          this.snackbarTitle = 'Unable to Save Workflow Folder.'
          this.snackbarImage = false
          this.showSnackbarBool = true
          return
        }
      }
      const permissionsData = this.$refs.permissions.buildPermissionsSaveDataJSON()

      this.snackbarCloseOnClick = false
      // check for decrypt password to match
      if (generalData.decryptPassword) {
        const decryptPayload = {
          vaultID: this.activeFolder.vaultID,
          password: generalData.decryptPassword,
        }
        decryptResult = await this.$store.dispatch('vault/decryptVault', decryptPayload)
      }
      if (decryptResult.sGenRes === '2') {
        this.showSnackbarBool = true
        this.snackbarTitle = 'ERROR: Password'
        this.snackbarSubTitle = 'Invalid Password.'
        this.snackbarImage = false
        this.isLoading = false
      } else if (generalData.parentFolderName.trim() === '') {
        this.showSnackbarBool = true
        this.snackbarTitle = 'ERROR: Parent Folder Name'
        this.snackbarSubTitle = 'Parent Folder Name cannot only include spaces.'
        this.snackbarImage = false
        this.isLoading = false
      } else if (!this.localShowOnlyFolderName && !generalData.driveStorageConfig) {
        this.showSnackbarBool = true
        this.snackbarTitle = 'ERROR: Drive Location'
        this.snackbarSubTitle = 'Drive Location cannot be empty.'
        this.snackbarImage = false
        this.isLoading = false
      } else {
          const folderType = this.isPortalParentFolder ? 5 : this.isInternalParentFolder ? 4 : 0
          let payload = {
            vaultID: this.activeFolder.vaultID,
            vaultName: generalData.parentFolderName,
            folderType: folderType,
          }
          
          // If we're only showing the folder name, we don't need to include other properties
          if (!this.localShowOnlyFolderName) {
            payload = {
              ...payload,
              defaultObject: generalData.defaultObject,
              piiData: generalData.containsPIIData,
              encrypted: generalData.encryptFolder,
              encryptionPassword: generalData.password,
              readOnly: generalData.readOnly,
              userAccess: permissionsData.assignedUsers,
              groupAccess: permissionsData.assignedGroups,
              storageConfigId: generalData.driveStorageConfig.value,
              userReadOnlyFolderSecurity: generalData.userReadOnlyFolderSecurity,
              groupReadOnlyFolderSecurity: generalData.groupReadOnlyFolderSecurity,
              autoVersioning: generalData.autoVersioning,
            }
          } else {
          // When only showing folder name, we need to preserve the existing values for these properties
          payload = {
            ...payload,
            defaultObject: this.activeFolder.defaultObject,
            piiData: this.activeFolder.piiData,
            encrypted: this.activeFolder.encrypted,
            encryptionPassword: this.activeFolder.encryptionPassword,
            readOnly: this.activeFolder.readOnly,
            userAccess: permissionsData.assignedUsers,
            groupAccess: permissionsData.assignedGroups,
            storageConfigId: this.activeFolder.storageConfigId,
            userReadOnlyFolderSecurity: this.activeFolder.userReadOnlyFolderSecurity || generalData.userReadOnlyFolderSecurity,
            groupReadOnlyFolderSecurity: this.activeFolder.groupReadOnlyFolderSecurity || generalData.groupReadOnlyFolderSecurity,
            autoVersioning: this.activeFolder.autoVersioning,
            parentFolder: this.parentFolderId
          }
          }
          result = await this.$store.dispatch('vault/saveVault', payload)
          
          const saveSuccess = result !== undefined && result !== null && result.errorCode < 1

          if (result?.errorCode > 0) {
            switch (result.errorCode) {
              case 1:
                this.snackbarSubTitle = 'Folder with same name exists.'
                break
              case 2:
                this.snackbarSubTitle = 'Error creating record in database.'
                break
              case 3:
                this.snackbarSubTitle = 'The folder was created, but there was a problem during encryption.'
                break
              case 4:
                this.snackbarCloseOnClick = true
                this.snackbarSubTitle = `An error occurred saving the folder to third party storage: ${result.errorMessage}`
                break
              case 5:
                this.snackbarSubTitle = 'Workflow folder can not be edited or deleted'
                break
              default:
                this.snackbarSubTitle = 'If problems continue to persist, please contact your Administrator.'
                break
            }
          }

        this.isLoading = false
        if (saveSuccess) {
          const snackbarTitle = 'Parent Folder Saved Successfully'
          let snackbarSubTitle = ''
          
          if (this.localShowOnlyFolderName) {
            snackbarSubTitle = `Saved "${generalData.parentFolderName}"`
          } else {
            snackbarSubTitle = `Saved "${generalData.parentFolderName}" to ${generalData.driveStorageConfig?.name ?? generalData.driveStorageConfig}`
          }
          
          this.markAsClean()
          this.$emit('saved', generalData.parentFolderName, generalData.driveStorageConfig, result.vaultID)
          if (!this.isModal) {
            this.$router.push({ name: 'ParentFolders', params: { saveSnackTitle: snackbarTitle, saveSnackSubtitle: snackbarSubTitle, saveSnackShowBool: true, lastActiveTab: this.parentFolderType } })
          }
        } else {
          this.snackbarTitle = 'Unable to Save Parent Folder'
          this.snackbarImage = false
          this.showSnackbarBool = true
        }
      }
    },
    async setComponentDetails(vaultID, localActiveFolder) {
      // the order of these calls are important - don't use promise.all
      await this.$store.dispatch('admin/setParentFolderPermissions', {})
      await this.setPermissionsDetails(vaultID, localActiveFolder)
      await this.setGeneralDetails(vaultID, localActiveFolder)
    },
    async setGeneralDetails(id, details) {
      const { general } = this.$refs
      await general.loadDriveList()
      if (id > 0) {
        await general.setValues(details)
      } else {
        await general.setValues()
      }
    },
    setParentFolderName(newName) { this.parentFolderName = newName },
    async setPermissionsDetails(id, details) {
      const { permissions } = this.$refs
      if (id > 0) {
        await permissions.setValues(details)
      } else {
        permissions.setValues()
      }
    },
    updateSelectedTab(newSelectedTab) { 
      this.selectedTabName = newSelectedTab 
    },
    toggleLeaveDialog() { this.showLeaveDialog = !this.showLeaveDialog },
  },
}
</script>

<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';

</style>
